/**
 * RuleEditDialog — create/edit a guardrail classification rule.
 *
 * IMPORTANT: UpdateGuardrailRuleRequest (quillAdminService.ts) only accepts
 * is_active | keywords | regex_patterns | verdict | priority | rule_name |
 * rule_description. `category` and `match_type` are NOT patchable by the
 * backend, same as rule_key. All three are shown read-only in edit mode and
 * are only ever set at creation time.
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';
import AdminEditDialogShell from '../../shared/AdminEditDialogShell';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors } from '../../../theme/designTokens';
import {
  GuardrailRule,
  RefusalTemplate,
  CreateGuardrailRuleRequest,
  UpdateGuardrailRuleRequest,
  GuardrailMatchType,
} from '../../../services/quillAdminService';
import { RULE_VERDICTS, MATCH_TYPES, matchTypeLabel } from './guardrailUi';

const ACCENT = colors.deepTeal[500];
const RULE_KEY_RE = /^[a-z0-9_]+$/;

function ChipInput({ values, onChange, placeholder }: { values: string[]; onChange: (v: string[]) => void; placeholder: string }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (!values.includes(t)) onChange([...values, t]);
    setDraft('');
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {values.map((v) => (
          <Chip key={v} label={v} size="small" onDelete={() => onChange(values.filter((x) => x !== v))} />
        ))}
      </Box>
      <TextField
        size="small"
        fullWidth
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        helperText="Press Enter to add"
      />
    </Box>
  );
}

function RegexListEditor({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');

  const validity = (pattern: string): string | null => {
    try {
      // eslint-disable-next-line no-new
      new RegExp(pattern);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid pattern';
    }
  };

  const add = () => {
    const t = draft.trim();
    if (!t) return;
    onChange([...values, t]);
    setDraft('');
  };

  return (
    <Box>
      {values.map((v, i) => {
        const err = validity(v);
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              value={v}
              error={!!err}
              onChange={(e) => {
                const next = [...values];
                next[i] = e.target.value;
                onChange(next);
              }}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              helperText={err || undefined}
            />
            <Box
              component="button"
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              sx={{ border: 'none', background: 'transparent', color: colors.error.main, cursor: 'pointer', fontSize: '1rem' }}
              aria-label="Remove pattern"
            >
              ✕
            </Box>
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          value={draft}
          placeholder="Add regex pattern"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
        <Box
          component="button"
          type="button"
          onClick={add}
          sx={{ border: `1px solid ${ACCENT}`, color: ACCENT, background: 'transparent', borderRadius: '8px', px: 2, cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem' }}
        >
          Add
        </Box>
      </Box>
    </Box>
  );
}

interface RuleEditDialogProps {
  mode: 'create' | 'edit';
  rule: GuardrailRule | null;
  templates: RefusalTemplate[];
  existingRuleKeys: string[];
  saving: boolean;
  onClose: () => void;
  onCreate: (payload: CreateGuardrailRuleRequest) => Promise<void> | void;
  onUpdate: (patch: UpdateGuardrailRuleRequest) => Promise<void> | void;
}

const RuleEditDialog: React.FC<RuleEditDialogProps> = ({
  mode,
  rule,
  templates,
  existingRuleKeys,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) => {
  const [ruleKey, setRuleKey] = useState(rule?.rule_key ?? '');
  const [ruleName, setRuleName] = useState(rule?.rule_name ?? '');
  const [category, setCategory] = useState(rule?.category ?? '');
  const [matchType, setMatchType] = useState<GuardrailMatchType>(rule?.match_type ?? 'fast_path_keyword');
  const [keywords, setKeywords] = useState<string[]>(rule?.keywords ?? []);
  const [regexPatterns, setRegexPatterns] = useState<string[]>(rule?.regex_patterns ?? []);
  const [verdict, setVerdict] = useState(rule?.verdict ?? RULE_VERDICTS[0]);
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [refusalTemplateId, setRefusalTemplateId] = useState(rule?.refusal_template_id ?? '');
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const ruleKeyValid = mode === 'edit' || (RULE_KEY_RE.test(ruleKey) && !existingRuleKeys.includes(ruleKey));
  const hasInvalidRegex = regexPatterns.some((p) => {
    try {
      // eslint-disable-next-line no-new
      new RegExp(p);
      return false;
    } catch {
      return true;
    }
  });
  const canSave = ruleKeyValid && ruleName.trim() && category.trim() && !hasInvalidRegex;

  const handleSaveClick = () => {
    if (!canSave) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (mode === 'create') {
      await onCreate({
        rule_key: ruleKey.trim(),
        rule_name: ruleName.trim(),
        category: category.trim(),
        match_type: matchType,
        keywords: keywords.length ? keywords : undefined,
        regex_patterns: regexPatterns.length ? regexPatterns : undefined,
        verdict,
        priority,
        refusal_template_id: refusalTemplateId || undefined,
      });
    } else {
      await onUpdate({
        rule_name: ruleName.trim(),
        keywords,
        regex_patterns: regexPatterns,
        verdict,
        priority,
        is_active: isActive,
      });
    }
    setConfirmOpen(false);
  };

  const title = mode === 'create' ? 'New Guardrail Rule' : `Edit ${rule?.rule_name ?? 'Rule'}`;

  return (
    <>
      <AdminEditDialogShell
        open
        onClose={onClose}
        accentColor={ACCENT}
        typeInitial={mode === 'create' ? '+' : 'R'}
        title={title}
        subtitle={mode === 'create' ? 'Fast-path or classifier-required rule' : rule?.rule_key ?? ''}
        onSave={handleSaveClick}
        isSaving={saving}
        isDirty
        maxWidth="md"
      >
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Rule changes take effect for live member conversations immediately.
          </Alert>

          <TextField
            label="Rule key"
            fullWidth
            size="small"
            value={ruleKey}
            disabled={mode === 'edit'}
            onChange={(e) => setRuleKey(e.target.value.toLowerCase())}
            error={mode === 'create' && ruleKey.length > 0 && !ruleKeyValid}
            helperText={
              mode === 'edit'
                ? 'Immutable once created'
                : 'Lowercase letters, numbers, underscores only — must be unique'
            }
            sx={{ mb: 2, ...(mode === 'edit' ? { '& .MuiInputBase-input': { fontFamily: 'monospace' } } : {}) }}
          />

          <TextField
            label="Rule name"
            fullWidth
            size="small"
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Category {mode === 'edit' && '(read-only — not patchable)'}
              </Typography>
              {mode === 'create' ? (
                <TextField size="small" fullWidth value={category} onChange={(e) => setCategory(e.target.value)} />
              ) : (
                <Chip label={category} size="small" variant="outlined" />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Match type {mode === 'edit' && '(read-only — not patchable)'}
              </Typography>
              {mode === 'create' ? (
                <Select size="small" fullWidth value={matchType} onChange={(e) => setMatchType(e.target.value as GuardrailMatchType)}>
                  {MATCH_TYPES.map((mt) => (
                    <MenuItem key={mt} value={mt}>
                      {matchTypeLabel(mt)}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Chip label={matchTypeLabel(matchType)} size="small" />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Keywords</Typography>
          <Box sx={{ mb: 2 }}>
            <ChipInput values={keywords} onChange={setKeywords} placeholder="Add a keyword and press Enter" />
          </Box>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Regex patterns</Typography>
          <Box sx={{ mb: 2 }}>
            <RegexListEditor values={regexPatterns} onChange={setRegexPatterns} />
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Verdict
              </Typography>
              <Select size="small" fullWidth value={verdict} onChange={(e) => setVerdict(e.target.value)}>
                {RULE_VERDICTS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ width: 140 }}>
              <TextField
                label="Priority"
                type="number"
                size="small"
                fullWidth
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
                helperText="Lower = first"
              />
            </Box>
          </Box>

          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Refusal template
          </Typography>
          <Select
            size="small"
            fullWidth
            displayEmpty
            value={refusalTemplateId}
            onChange={(e) => setRefusalTemplateId(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">None</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.template_id} value={t.template_id}>
                {t.template_key}
              </MenuItem>
            ))}
          </Select>

          {mode === 'edit' && (
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label="Rule is active"
            />
          )}
        </Box>
      </AdminEditDialogShell>

      <QuillActionConfirm
        open={confirmOpen}
        title={mode === 'create' ? 'Create guardrail rule' : 'Save rule changes'}
        message={
          mode === 'create'
            ? 'This adds a new live classification rule that evaluates real member queries immediately (production).'
            : 'This changes how a live classification rule evaluates real member queries immediately (production).'
        }
        confirmLabel="Confirm & Save"
        loading={saving}
        pendingLabel="Applying…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default RuleEditDialog;
